import {
    ArchiveOutlined,
    CheckCircleOutlined,
    DescriptionOutlined,
    HandymanOutlined,
    HistoryEduOutlined,
} from '@mui/icons-material';
import {FC, ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {StatsStripTone} from 'src/components/common/StatsStrip/StatsStrip.types';
import {ContractStatusEnum} from 'src/enums';
import {ContractStatsStripType} from './ContractStatsStrip.types';

/**
 * The four statuses a contract can be in, in the order it passes through them.
 *
 * The frontend enum carries two more — REJECTED and VALIDATION — which the server's own
 * ContractStatus does not have, so no contract can ever be counted under them.
 */
const STRIP_STATUSES: ContractStatusEnum[] = [
    ContractStatusEnum.WAIT_FOR_SIGNATURE,
    ContractStatusEnum.INSTALLATION,
    ContractStatusEnum.OPERATION,
    ContractStatusEnum.ARCHIVED,
];

const STATUS_ICONS: Record<string, ReactNode> = {
    [ContractStatusEnum.WAIT_FOR_SIGNATURE]: <HistoryEduOutlined/>,
    [ContractStatusEnum.INSTALLATION]: <HandymanOutlined/>,
    [ContractStatusEnum.OPERATION]: <CheckCircleOutlined/>,
    [ContractStatusEnum.ARCHIVED]: <ArchiveOutlined/>,
};

const STATUS_TONES: Record<string, StatsStripTone> = {
    [ContractStatusEnum.WAIT_FOR_SIGNATURE]: 'warning',
    [ContractStatusEnum.INSTALLATION]: 'secondary',
    [ContractStatusEnum.OPERATION]: 'success',
    // Not a problem, just over — the same neutral the unassigned cards wear.
    [ContractStatusEnum.ARCHIVED]: 'neutral',
};

export const ContractStatsStrip: FC<ContractStatsStripType> = ({
                                                                   data,
                                                                   activeStatus,
                                                                   onSelectStatus,
                                                               }) => {
    const intl = useIntl();

    const subtitle = intl.formatMessage(
        {id: 'contractStatsSubtitle'},
        {contracts: data?.totalContracts ?? 0}
    );

    return (
        <StatsStrip
            icon={<DescriptionOutlined/>}
            title="contracts"
            subtitle={subtitle}
            tiles={STRIP_STATUSES.map((status) => ({
                key: status,
                label: status,
                value: data?.byStatus?.[status] ?? (data ? 0 : undefined),
                icon: STATUS_ICONS[status],
                tone: STATUS_TONES[status],
                active: activeStatus === status,
                onClick: onSelectStatus ? () => onSelectStatus(status) : undefined,
            }))}
        />
    );
};
